import React, { useState } from 'react';
import Modal from '../common/Modal';
import './ClientModal.css';

const ClientModal = ({ isOpen, onClose, clients, onSelectClient, onCreateClient }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    passportNumber: '',
    passportExpiry: '',
    passportCountry: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: ''
  });
  const [errors, setErrors] = useState({});

  const filteredClients = clients.filter(client => {
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    const phone = (client.phone || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return fullName.includes(search) || email.includes(search) || phone.includes(search);
  });

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
    const newErrors = {};
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Format data for API
    const clientData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      date_of_birth: formData.dateOfBirth || null,
      passport_number: formData.passportNumber || null,
      passport_expiry: formData.passportExpiry || null,
      passport_country: formData.passportCountry || null,
      emergency_contact_name: formData.emergencyContactName || null,
      emergency_contact_phone: formData.emergencyContactPhone || null,
      emergency_contact_relation: formData.emergencyContactRelation || null
    };

    await onCreateClient(clientData);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showCreateForm ? 'Create New Client' : 'Select Client'}
      maxWidth="700px"
    >
      <div className="client-modal-body">
          {!showCreateForm ? (
            <>
              {/* Client Selection View */}
              <div className="client-modal-actions">
                <input
                  type="text"
                  placeholder="Search clients by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create New Client
                </button>
              </div>

              <div className="client-list">
                {filteredClients.length === 0 ? (
                  <p className="no-clients">
                    {searchTerm ? 'No clients found matching your search.' : 'No clients found. Create a new client to get started.'}
                  </p>
                ) : (
                  filteredClients.map(client => (
                    <div 
                      key={client.id} 
                      className="client-item"
                      onClick={() => {
                        onSelectClient({
                          ...client,
                          firstName: client.first_name,
                          lastName: client.last_name
                        });
                        onClose();
                      }}
                    >
                      <div className="client-item-main">
                        <strong>{client.first_name} {client.last_name}</strong>
                        <span className="client-email">{client.email}</span>
                      </div>
                      <div className="client-item-sub">
                        <span>{client.phone}</span>
                        {client.quote_count > 0 && (
                          <span className="quote-count">{client.quote_count} quotes</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              {/* Create Client Form */}
              <form onSubmit={handleCreateClient} className="create-client-form">
                <h4>Personal Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className={errors.phone ? 'error' : ''}
                    />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Street address, city, state, zip"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                    />
                  </div>
                </div>

                <h4>Travel Documents</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Passport Number</label>
                    <input
                      type="text"
                      value={formData.passportNumber}
                      onChange={(e) => setFormData({...formData, passportNumber: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Passport Expiry</label>
                    <input
                      type="date"
                      value={formData.passportExpiry}
                      onChange={(e) => setFormData({...formData, passportExpiry: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Passport Country</label>
                    <input
                      type="text"
                      value={formData.passportCountry}
                      onChange={(e) => setFormData({...formData, passportCountry: e.target.value})}
                    />
                  </div>
                </div>

                <h4>Emergency Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      type="text"
                      value={formData.emergencyContactName}
                      onChange={(e) => setFormData({...formData, emergencyContactName: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="tel"
                      value={formData.emergencyContactPhone}
                      onChange={(e) => setFormData({...formData, emergencyContactPhone: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Relationship</label>
                    <input
                      type="text"
                      value={formData.emergencyContactRelation}
                      onChange={(e) => setFormData({...formData, emergencyContactRelation: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Back to List
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Client
                  </button>
                </div>
              </form>
            </>
          )}
      </div>
    </Modal>
  );
};

export default ClientModal;
